import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import { Check, Copy, Info, Save, LogIn, Plus, Trash2, Pencil, ChevronDown } from "lucide-react"
import { useState, useRef, useEffect, memo } from 'react'
import { logUsage } from '@/lib/usageLog'
import { logError } from '@/lib/sentry'
import { useAuth } from "@/hooks/useAuth"
import { useSubscription } from "@/contexts/SubscriptionContext"
import { cn } from "@/lib/utils"
import { MemberSetting } from "@/types/purchase"
import type { MessageTemplate } from "@/types/messageTemplate"
import { supabase } from "@/supabase/client"
import { PaymentService } from "@/supabase/payment/paymentService"
import { checkNonMemberUsage, incrementNonMemberUsage } from '@/lib/pseudoUser' // Added
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog" // Added
import { LockKeyhole, AlertCircle } from "lucide-react" // Added AlertCircle for dialog icon
import { toast } from "sonner"

export interface MessageTemplateSectionProps {
  template: string;
  setTemplate: (template: string) => void;
  generatedMessage: string;
  resetOrder: () => void;
  titleStyle?: string; // Made optional
  activeCustomerName?: string;
  hasProducts?: boolean;
  memberSetting: MemberSetting;
  setMemberSetting: (setting: ((prev: MemberSetting) => MemberSetting) | MemberSetting) => void;
  shippingFee: number;
  setShippingFee: (fee: number) => void;
  freeShippingThreshold: number;
  setFreeShippingThreshold: (threshold: number) => void;
  openLoginModal?: () => void;
  memberSettings: MemberSetting[];
  setMemberSettings: (settings: MemberSetting[]) => void;
  // Props to control visibility of editor panels, will be managed by Home.tsx
  showTemplateEditorProp?: boolean; // Renamed to avoid conflict with internal state for now
  setShowTemplateEditorProp?: (show: boolean) => void; // Renamed
  showShippingSettingsProp?: boolean; // Renamed
  setShowShippingSettingsProp?: (show: boolean) => void; // Renamed
  subscriptionPlanId?: string; // Added prop
  calculatedShippingFee: number; // Added: use parent's computed fee for preview (empty products 포함)
  templates: MessageTemplate[];
  activeTemplateId: number | null;
  onSelectTemplate: (id: number) => void;
  isAtMaxLimit: boolean;
  isAtMinLimit: boolean;
  onCreateTemplate: (name: string, content: string) => Promise<MessageTemplate | null>;
  onRenameActive: (newName: string) => Promise<void>;
  onDeleteActive: () => Promise<void>;
  onUpdateActiveContent: (content: string) => Promise<void>;
  // 다른 배송비 드래프트 캐시(Home 단일 ref 주입) — lg 메시지 탭 전환 등 리마운트에도 미저장 입력 보존.
  // base = 드래프트가 기반한 커밋본 identity: 언마운트 중 하이드레이션이 일어났으면 stale 드래프트를 복원하지 않기 위함.
  presetDraftsRef: { current: { base: number[]; drafts: string[] } | null };
}

// 새 템플릿 생성 시 pre-fill될 예시 내용 + textarea placeholder의 base.
// 변수 사용법을 한눈에 보여주는 가이드 역할.
const EXAMPLE_TEMPLATE = `[고객 닉네임]
주문 감사합니다 🙏

[상품목록]

상품 합계: [금액]
배송비: [배송비]
총 결제 금액: [총금액]

[입금계좌]`;

function MessageTemplateSectionImpl({
  template,
  setTemplate,
  generatedMessage,
  resetOrder,
  activeCustomerName,
  hasProducts = true,
  memberSetting,
  setMemberSetting,
  shippingFee,
  setShippingFee,
  freeShippingThreshold,
  setFreeShippingThreshold,
  openLoginModal,
  memberSettings,
  setMemberSettings,
  // Props for visibility control (will eventually replace internal state)
  showTemplateEditorProp,
  showShippingSettingsProp,
  subscriptionPlanId,
  calculatedShippingFee,
  templates,
  activeTemplateId,
  onSelectTemplate,
  isAtMaxLimit,
  isAtMinLimit,
  onCreateTemplate,
  onRenameActive,
  onDeleteActive,
  onUpdateActiveContent,
  presetDraftsRef
}: MessageTemplateSectionProps) {
  const [isCopied, setIsCopied] = useState(false)
  // 다른 배송비 입력은 문자열 드래프트로 관리 — 숫자 state 직접 바인딩 시 빈칸이 0으로 되살아나
  // 0을 지울 수 없고(Number('')===0) '02000' 표시가 남는 버그 방지. 저장 시에만 파싱·검증.
  // 초기값: 캐시 ref에 같은 커밋본 기반 드래프트가 있으면 복원(lg 탭 전환 리마운트 생존, codexloop 후속 P1).
  const [presetDrafts, setPresetDrafts] = useState<string[]>(() =>
    presetDraftsRef.current && presetDraftsRef.current.base === memberSetting.shippingFeePresets
      ? presetDraftsRef.current.drafts
      : memberSetting.shippingFeePresets.map(String)
  );
  // 커밋본(로드/저장/프로파일 전환)이 '바뀔 때만' 드래프트 재동기화 — 마운트 첫 실행은 스킵해
  // 방금 복원한 드래프트를 지우지 않는다. 타이핑은 드래프트만 갱신하므로 충돌 없음.
  const presetBaseRef = useRef(memberSetting.shippingFeePresets);
  useEffect(() => {
    if (presetBaseRef.current === memberSetting.shippingFeePresets) return;
    presetBaseRef.current = memberSetting.shippingFeePresets;
    setPresetDrafts(memberSetting.shippingFeePresets.map(String));
  }, [memberSetting.shippingFeePresets]);
  // 드래프트를 캐시 ref에 동기화 — 리마운트 복원용. 하이드레이션 직후 한 커밋은 (new base, old drafts)로
  // 기록될 수 있으나 곧바로 재동기화 렌더에서 교정된다(둘 다 이 effect가 다시 쓴다).
  useEffect(() => {
    presetDraftsRef.current = { base: presetBaseRef.current, drafts: presetDrafts };
  }, [presetDraftsRef, presetDrafts, memberSetting.shippingFeePresets]);
  const [showNonMemberCopyLimitDialog, setShowNonMemberCopyLimitDialog] = useState(false);
  const [showMemberCopyLimitDialog, setShowMemberCopyLimitDialog] = useState(false); // Added for member limit
  const [showPastDueDialog, setShowPastDueDialog] = useState(false); // Added for past_due
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // 새 템플릿 만들기 다이얼로그. window.prompt() 대체용.
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  // Radix AlertDialog는 기본적으로 Cancel 버튼에 포커스를 둠 — Enter가 dialog dismiss로
  // 흘러가지 않게 input에 명시적으로 focus + select. onOpenAutoFocus의 default를 막고 직접 처리.
  const newTemplateInputRef = useRef<HTMLInputElement | null>(null);
  // 변수 목록 섹션 접힘 상태. 기본 false (접힘) — placeholder가 이미 예시를 보여주므로 보조 정보.
  const [showVariablesPanel, setShowVariablesPanel] = useState(false);
  // 활성 탭의 이름을 inline으로 편집 중일 때만 set. null이면 표시 모드.
  const [editingTabId, setEditingTabId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const editingInputRef = useRef<HTMLInputElement | null>(null);
  // Internal state for now, will be controlled by props later
  const [showTemplateEditorInternal] = useState(false); // Removed setShowTemplateEditorInternal
  const [showShippingSettingsInternal] = useState(false); // Removed setShowShippingSettingsInternal
  const buttonRef = useRef<HTMLButtonElement>(null)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // 활성 탭이 바뀌면 inline 편집 모드 자동 종료 (혹시 편집 중이었어도 cancel)
  useEffect(() => {
    setEditingTabId(null);
  }, [activeTemplateId]);

  // 활성 탭 inline 이름 편집 시작 — 펜 아이콘 클릭 시 호출
  const startEditingActiveName = () => {
    const active = templates.find(t => t.id === activeTemplateId);
    if (!active) return;
    setEditingTabId(active.id);
    setEditingName(active.name);
    // 다음 tick에 input focus + select
    setTimeout(() => {
      editingInputRef.current?.focus();
      editingInputRef.current?.select();
    }, 0);
  };

  // 편집 완료 시 호출 (Enter, blur). 변경된 경우에만 onRenameActive 호출.
  const commitEditingName = async () => {
    const targetId = editingTabId;
    if (targetId == null) return;
    const trimmed = editingName.trim();
    setEditingTabId(null);
    if (!trimmed) return; // 빈 이름은 저장 안 함 (취소와 동일)
    const target = templates.find(t => t.id === targetId);
    if (!target || trimmed === target.name) return;
    try {
      await onRenameActive(trimmed);
    } catch {
      toast.error('템플릿 이름 변경에 실패했습니다.');
    }
  };

  const cancelEditingName = () => {
    setEditingTabId(null);
  };

  // 변수 chip 클릭 시 textarea 커서 위치(또는 끝)에 코드 삽입.
  const handleInsertVariable = (code: string) => {
    const ta = textareaRef.current;
    const currentValue = template;
    if (!ta) {
      // ref 없으면 끝에 append
      handleTemplateChange(currentValue + code);
      return;
    }
    const start = ta.selectionStart ?? currentValue.length;
    const end = ta.selectionEnd ?? currentValue.length;
    const next = currentValue.slice(0, start) + code + currentValue.slice(end);
    handleTemplateChange(next);
    // 커서를 삽입한 코드 끝으로 이동 (다음 paint 후)
    requestAnimationFrame(() => {
      ta.focus();
      const cursor = start + code.length;
      try { ta.setSelectionRange(cursor, cursor); } catch {/* ignore */}
    });
  };

  // + 버튼 클릭 — 다이얼로그 열고 기본 이름 pre-fill.
  // window.prompt() 대신 shadcn AlertDialog 사용 (모바일/IME 안정성).
  const handleOpenCreateDialog = () => {
    setNewTemplateName('새 안내');
    setShowCreateDialog(true);
  };

  // 다이얼로그 "추가" 버튼 또는 Enter 키 — 실제 onCreateTemplate 호출.
  // 새 템플릿은 예시 내용으로 pre-fill하여 사용자가 빈 화면에서 시작하지 않도록.
  // 보류 중인 debounce save를 먼저 flush — activeId가 새 템플릿으로 바뀐 뒤
  // 옛 입력이 새 템플릿을 덮어쓰는 race 차단.
  const handleConfirmCreateTemplate = async () => {
    const trimmed = newTemplateName.trim();
    if (!trimmed) return;
    setShowCreateDialog(false);
    await flushPendingContentSave();
    const created = await onCreateTemplate(trimmed, EXAMPLE_TEMPLATE);
    if (!created) {
      toast.error('템플릿 추가에 실패했습니다.');
      return;
    }
    // useEffect chain만 의존하면 propagate가 누락되는 케이스가 보고됨.
    // 직접 setTemplate으로 textarea + 미리보기 즉시 동기화 보강 (생성 직후 한 번만).
    setTemplate(created.content);
  };

  // 언마운트 시 debounce timeout 정리
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  // textarea 변경 시: 즉시 미리보기 반영 + debounced DB 저장
  const handleTemplateChange = (value: string) => {
    setTemplate(value); // 즉시 미리보기 반영
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      onUpdateActiveContent(value).catch(() => {
        // 디바운스 자동 저장 실패는 사용자에게 알려야 함 — 화면에 보이는 텍스트와
        // 영구 저장 사이 차이가 데이터 손실로 이어질 수 있어서.
        toast.error('템플릿 자동 저장에 실패했습니다. 잠시 후 다시 입력해 주세요.');
      });
    }, 800);
  };

  // 보류 중인 debounce 저장을 OLD activeTemplateId 기준으로 즉시 flush.
  // (onUpdateActiveContent는 호출 시점의 activeTemplateId를 읽으므로 activeId가
  //  바뀌기 전에 await 해야 OLD 템플릿에 저장됨. tab 전환 / 새 템플릿 생성 / 삭제
  //  등 activeId가 바뀌는 모든 경로 직전에 호출.)
  const flushPendingContentSave = async () => {
    if (!saveTimeoutRef.current) return;
    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = null;
    try {
      await onUpdateActiveContent(template);
    } catch {
      toast.error('템플릿 저장에 실패하여 일부 변경이 반영되지 않았을 수 있습니다.');
    }
  };

  const handleTabClick = async (id: number) => {
    if (id === activeTemplateId) return;
    await flushPendingContentSave();
    onSelectTemplate(id);
  };
  const { user, pseudoUserId } = useAuth()
  const {
    isCopyLimitReached,
    incrementCopyCount,
    isPastDue
  } = useSubscription();

  // 변수 코드와 설명을 쌍으로 정의
  const templateVariables = [
    { code: "[상품목록]", description: "주문 상품 이름과 가격" },
    { code: "[총건수]", description: "총 구매 건수 = 상품 수량 합계 (예: 7건)" },
    { code: "[금액]", description: "상품 합계" },
    { code: "[배송비]", description: "자동 계산된 배송비" },
    { code: "[총금액]", description: "상품 합계 + 배송비" },
    { code: "[입금계좌]", description: "등록한 입금 계좌" },
    { code: "[고객 닉네임]", description: "고객 닉네임 + '님' (예: 홍길동님)" },
  ]

  // 사용자 설정 저장
  const handleSaveClick = async () => {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      openLoginModal?.();
      return;
    }

    // 이모지 자동 저장이 pending 중이면 timer 취소 — 명시적 저장이 모든 필드를 덮어쓰므로
    // 뒤늦게 발화한 silent save가 이 commit을 stale 값으로 덮는 race 차단.
    if (emojiSaveTimeoutRef.current) {
      clearTimeout(emojiSaveTimeoutRef.current);
      emojiSaveTimeoutRef.current = null;
    }

    // 프리셋 validation을 이름 prompt 전에 수행 — 검증 실패면 prompt 자체를 띄우지 않음 (codex R1 P2).
    // 소스는 문자열 드래프트 — 여기서만 숫자로 파싱한다(빈칸/비숫자는 NaN → 거부).
    const parsed = presetDrafts.map((d) => (d.trim() === '' ? NaN : Number(d)));
    if (parsed.some((p) => !Number.isInteger(p) || p <= 0)) {
      alert('다른 배송비는 1원 이상의 정수만 등록할 수 있습니다. (빈 칸은 삭제해주세요)');
      return;
    }
    const presets = [...parsed].sort((a, b) => a - b);
    if (presets.length > 4) {
      alert('다른 배송비는 최대 4개까지 등록할 수 있습니다.');
      return;
    }
    // 제주 배송비와의 중복은 허용(오너 2026-07-12) — 표시 우선순위(프리셋>제주)가 결정적이고 동작은 값 동일.
    const reserved = new Set([shippingFee]);
    if (new Set(presets).size !== presets.length || presets.some((p) => reserved.has(p))) {
      alert('다른 배송비는 서로, 그리고 기본 배송비와 중복될 수 없습니다.');
      return;
    }

    const settingName = prompt("저장할 설정의 이름을 입력해주세요:", memberSetting.settingName || "새 설정");
    if (settingName) {
      saveMemberSetting(settingName, presets);
    }
  };

  const saveMemberSetting = async (settingName: string, presets: number[]) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        alert('로그인이 필요합니다.');
        return;
      }

      // presets는 handleSaveClick이 드래프트에서 파싱·검증·정렬해 넘긴 값.
      const newSetting = {
        shipping_fee: shippingFee,
        free_shipping_threshold: freeShippingThreshold,
        deposit_account: memberSetting.depositAccount,
        message_template: template,
        setting_name: settingName,
        user_id: session.user.id,
        item_emoji: memberSetting.itemEmoji,
        jeju_shipping_fee: memberSetting.jejuShippingFee,
        jeju_free_shipping_fee: memberSetting.jejuFreeShippingFee,
        shipping_fee_presets: presets,
      };

      // 항상 upsert로 처리
      const { data, error } = await supabase
        .from('member_setting')
        .upsert({
          id: memberSetting.id,  // 있으면 수정, 없으면 새로 생성
          ...newSetting
        })
        .select()
        .single();

      if (error) throw error;

      // 저장된 설정으로 상태 업데이트
      const savedSetting = {
        id: data.id,
        shippingFee: data.shipping_fee,
        freeShippingThreshold: data.free_shipping_threshold,
        depositAccount: data.deposit_account,
        messageTemplate: data.message_template,
        settingName: data.setting_name,
        itemEmoji: data.item_emoji,
        jejuShippingFee: data.jeju_shipping_fee,
        jejuFreeShippingFee: data.jeju_free_shipping_fee ?? 0,
        shippingFeePresets: Array.isArray(data.shipping_fee_presets) ? data.shipping_fee_presets : []
      };

      // memberSetting 업데이트
      setMemberSetting(savedSetting);

      // memberSettings 배열 업데이트
      if (memberSetting.id) {
        // 기존 설정 업데이트
        setMemberSettings(
          memberSettings.map(setting => setting.id === savedSetting.id ? savedSetting : setting)
        );
      } else {
        // 새 설정 추가
        setMemberSettings([savedSetting, ...memberSettings]);
      }

      alert('설정이 저장되었습니다.');
    } catch (error) {
      logError('설정 저장에 실패했습니다:', error);
      alert('설정 저장에 실패했습니다.');
    }
  };

  // 이모지 자동 저장 (silent) — debounce(500ms)로 묶어 rapid emoji 클릭 시 DB write 절약.
  // 실패 시 logError만 (toast X). 비로그인/row 미존재 시 silent skip.
  //
  // 의도적으로 item_emoji 하나만 PATCH (full row upsert X):
  //   1) shipping_fee 등 다른 필드를 stale closure 값으로 덮는 race 차단
  //   2) memberSetting.id 미존재 상태에서 여러 silent save가 겹치며 row 중복 생성 차단
  //
  // memberSetting.id가 없으면 (한 번도 명시적 저장한 적 없는 사용자) silent save는
  // 스킵하고, 사용자가 "현재 설정 저장"을 누를 때 일괄 저장된다.
  const emojiSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const saveEmojiSilent = async (emoji: string, settingId: string) => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('member_setting')
        .update({ item_emoji: emoji })
        .eq('id', settingId);

      if (error) throw error;
    } catch (error) {
      logError('이모지 자동 저장 실패:', error);
    }
  };

  // 이모지 변경 통합 진입점. setMemberSetting + debounced silent save.
  const applyEmoji = (value: string) => {
    setMemberSetting((prev) => ({ ...prev, itemEmoji: value }));
    if (emojiSaveTimeoutRef.current) clearTimeout(emojiSaveTimeoutRef.current);
    // settingId snapshot은 발화 시점에 다시 읽어 최신 state 사용.
    emojiSaveTimeoutRef.current = setTimeout(() => {
      const id = memberSetting.id;
      if (!user || id == null) return; // row 없으면 명시적 저장 시 일괄 반영
      saveEmojiSilent(value, id);
    }, 500);
  };

  // 언마운트 시 emoji debounce timer 정리
  useEffect(() => {
    return () => {
      if (emojiSaveTimeoutRef.current) clearTimeout(emojiSaveTimeoutRef.current);
    };
  }, []);

  const handleCopyToClipboard = () => {
    // 비회원 로직 (기존과 유사하게 유지, 비동기 처리)
    if (!user) {
      const { canUse } = checkNonMemberUsage(pseudoUserId, 'message_copy', 3);
      if (!canUse) {
        setShowNonMemberCopyLimitDialog(true);
        return;
      }

      navigator.clipboard.writeText(generatedMessage).then(() => {
        setIsCopied(true);
        incrementNonMemberUsage(pseudoUserId, 'message_copy');
        logUsage({ userId: undefined, pseudoUserId, isMember: false, type: 'clipboard' });
        setTimeout(() => setIsCopied(false), 1500);
      }).catch(err => {
        logError('클립보드 복사 실패 (비회원):', err);
        alert('메시지 복사에 실패했습니다. 모바일 환경에서는 브라우저가 복사를 지원하지 않을 수 있습니다.');
      });
      return;
    }

    // --- 회원 로직 (상태 기반 동기적 처리) ---

    // 0. past_due 상태이면 복사 금지
    if (isPastDue) {
      setShowPastDueDialog(true);
      return;
    }

    // 1. 로컬 상태로 복사 가능 여부 즉시 확인
    if (isCopyLimitReached) {
      setShowMemberCopyLimitDialog(true);
      return;
    }

    // 2. `await` 없이 즉시 클립보드에 복사
    navigator.clipboard.writeText(generatedMessage).then(() => {
      // 3. 복사 성공 시 UI 및 로컬 상태 즉시 업데이트 (낙관적 업데이트)
      setIsCopied(true);
      incrementCopyCount(); // 컨텍스트의 로컬 상태 업데이트
      setTimeout(() => setIsCopied(false), 1500);

      // 4. 백그라운드에서 서버에 실제 사용량 업데이트 및 로깅 (비동기 후처리)
      const paymentService = new PaymentService();
      paymentService.incrementCopyUsage(user.id).then(usageResult => {
        if (!usageResult.success) {
          // 서버 업데이트 실패 시 에러 로깅 (UI는 이미 업데이트되었으므로 사용자에게는 알리지 않음)
          logError('서버 복사 사용량 증가 실패:', { userId: user.id, reason: usageResult.reason });
          // 필요하다면 여기서 로컬 상태를 다시 동기화하는 로직 추가 가능 (예: refreshSubscription())
        }
      }).catch(error => {
        logError('서버 복사 사용량 증가 중 예외 발생:', { userId: user.id, error });
      });

      logUsage({
        userId: user.id,
        pseudoUserId,
        isMember: true,
        type: 'clipboard'
      });

    }).catch(err => {
      // 클립보드 복사 자체가 실패한 경우 (예: 브라우저 권한 없음)
      logError('클립보드 복사 실패 (회원):', { userId: user.id, error: err });
      alert('메시지 복사에 실패했습니다. 브라우저의 클립보드 접근 권한을 확인해주세요.');
    });
  };

  // 상품이 없는 경우에도 메시지를 생성하는 함수
  const generateEmptyProductsMessage = () => {
    // 기본 템플릿에서 시작
    let message = template;

    // [고객 닉네임] 변수 치환
    // 고객 이름이 없는 경우 빈 문자열로 치환됩니다. '님'은 고객 이름이 있을 때만 추가됩니다.
    message = message.replace(/\[고객 닉네임\]/g, activeCustomerName ? `${activeCustomerName}님` : "");

    // [상품목록]을 상품 추가 안내 메시지로 치환
    message = message.replace(
      /\[상품목록\]/g,
      "상품을 추가하면 이 영역에 상품 목록이 표시됩니다."
    );

    // 다른 변수들은 실제 값으로 치환
    message = message
      .replace(/\[금액\]/g, "0원")
      .replace(/\[배송비\]/g, calculatedShippingFee === 0 ? "0원" : `${calculatedShippingFee.toLocaleString()}원`)
      .replace(/\[총금액\]/g, calculatedShippingFee.toLocaleString() + "원")
      .replace(/\[총건수\]/g, "0건")
      .replace(/\[입금계좌\]/g, memberSetting.depositAccount || "");

    return message;
  };

  // Determine which visibility flag to use (prop or internal state)
  // This is a temporary step until state is fully lifted.
  const displayTemplateEditor = showTemplateEditorProp !== undefined ? showTemplateEditorProp : showTemplateEditorInternal;
  const displayShippingSettings = showShippingSettingsProp !== undefined ? showShippingSettingsProp : showShippingSettingsInternal;

  return (
    // The outer div with "rounded-2xl bg-white" and the header "p-5 border-b" were removed in a previous step.
    // This component now expects to be placed inside a CardContent.
    <div className="space-y-5">
      {/* 배송/결제 설정 패널 */}
      {displayShippingSettings && (
        <div className="bg-slate-50 rounded-xl p-5 border border-slate-200 shadow-sm">
          <div className="flex items-start mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <Info className="w-5 h-5 text-blue-600 mr-2.5 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-700">
              배송비 및 입금 계좌 정보를 설정하세요.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
            {/* 기본 배송비 */}
            <div className="space-y-1.5">
              <label htmlFor="shippingFeeInput" className="text-sm font-medium text-gray-700 block">기본 배송비 (원)</label>
              <Input
                id="shippingFeeInput"
                type="number"
                step="500"
                value={shippingFee}
                onChange={(e) => setShippingFee(Number(e.target.value))}
                className="w-full text-sm px-3 py-2 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="예: 3000"
              />
              <p className="text-xs text-gray-500">
                상품 총액이 무료 배송 기준 미만일 때 적용됩니다.
              </p>
            </div>

            {/* 제주/산간 배송비 */}
            <div className="space-y-1.5">
              <label htmlFor="jejuShippingFeeInput" className="text-sm font-medium text-gray-700 block">제주/산간 배송비 (원)</label>
              <Input
                id="jejuShippingFeeInput"
                type="number"
                step="500"
                value={memberSetting.jejuShippingFee}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setMemberSetting((prev) => ({
                    ...prev,
                    jejuShippingFee: value
                  }) as MemberSetting);
                }}
                className="w-full text-sm px-3 py-2 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="예: 6000"
              />
              <p className="text-xs text-gray-500">
                제주/산간 지역 고객에게 적용되는 배송비입니다.
              </p>
            </div>

            {/* 배송비 무료 기준 금액 */}
            <div className="space-y-1.5">
              <label htmlFor="freeShippingThresholdInput" className="text-sm font-medium text-gray-700 block">무료 배송 기준 금액 (원)</label>
              <Input
                id="freeShippingThresholdInput"
                type="number"
                step="1000"
                value={freeShippingThreshold}
                onChange={(e) => setFreeShippingThreshold(Number(e.target.value))}
                className="w-full text-sm px-3 py-2 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="예: 50000"
              />
              <p className="text-xs text-gray-500">
                이 금액 이상 구매 시 배송비가 무료입니다. (무료배송이 없다면 충분히 큰 금액을 입력하세요. 예) 999,999)
              </p>
            </div>

            {/* 무료배송 시 제주/산간 배송비 */}
            <div className="space-y-1.5">
              <label htmlFor="jejuFreeShippingFeeInput" className="text-sm font-medium text-gray-700 block">무료배송 시 제주 배송비 (원)</label>
              <Input
                id="jejuFreeShippingFeeInput"
                type="number"
                step="500"
                min={0}
                value={memberSetting.jejuFreeShippingFee}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (Number.isFinite(val) && val >= 0) {
                    setMemberSetting((prev) => ({ ...prev, jejuFreeShippingFee: val }));
                  }
                }}
                className="w-full text-sm px-3 py-2 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="예: 3000"
              />
              <p className="text-xs text-gray-500">
                무료 배송과 별개로 따로 제주/산간 배송비를 받으시는 경우 설정하세요.
              </p>
            </div>

            {/* 입금 계좌 */}
            <div className="space-y-1.5 md:col-span-2">
              <label htmlFor="depositAccountInput" className="text-sm font-medium text-gray-700 block">입금 계좌 정보</label>
              <Input
                id="depositAccountInput"
                value={memberSetting.depositAccount}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setMemberSetting((prev) => ({
                    ...prev,
                    depositAccount: newValue
                  }));
                }}
                placeholder="예: 카카오뱅크 123-456-7890 (예금주: 홍길동)"
                className="w-full text-sm px-3 py-2 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="text-xs text-gray-500">
                메시지에 포함될 입금 계좌 정보를 정확히 입력하세요.
              </p>
            </div>

            {/* 다른 배송비 (프리셋) — 그리드 마지막 전폭 셀. 기존 5개 필드 위치 불변 (불변식 1: 미등록 셀러 UI 불변).
                미등록 셀러는 '배송비 추가' 버튼 1개만 보임 — 라벨·헬퍼는 프리셋이 생긴 뒤에만 나타남. */}
            <div className="space-y-1.5 md:col-span-2">
              {presetDrafts.length > 0 && (
                <label className="text-sm font-medium text-gray-700 block">다른 배송비 (원)</label>
              )}
              {presetDrafts.map((draft, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="500"
                    min={0}
                    value={draft}
                    aria-label={`다른 배송비 ${idx + 1}`}
                    onChange={(e) => {
                      const v = e.target.value;
                      setPresetDrafts((prev) => prev.map((d, i) => (i === idx ? v : d)));
                    }}
                    className="w-full text-sm px-3 py-2 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="예: 6000"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-label={`다른 배송비 ${idx + 1} 삭제`}
                    className="flex-shrink-0 h-9 w-9 p-0 border-gray-300 text-gray-500 hover:bg-gray-100 hover:text-red-600"
                    onClick={() => setPresetDrafts((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              {presetDrafts.length < 4 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9 border-gray-300 text-gray-700 hover:bg-gray-100"
                  onClick={() => setPresetDrafts((prev) => [...prev, ''])}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> 다른 배송비 추가
                </Button>
              )}
              {presetDrafts.length > 0 && (
                <p className="text-xs text-gray-500">
                  기본 배송비 외에 다른 금액을 받는 상품이 있다면 등록하세요. 정산서 화면 배송비 토글에 금액 버튼으로 나타나 클릭 한 번으로 적용됩니다.
                </p>
              )}
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-gray-200 flex justify-end">
            {user ? (
              <Button
                size="sm"
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 text-sm font-medium rounded-md transition-colors"
                onClick={handleSaveClick}
              >
                <Save className="h-4 w-4 mr-2" />
                현재 설정 저장
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="text-indigo-600 border-indigo-300 hover:bg-indigo-50 px-4 py-2 text-sm font-medium rounded-md transition-colors"
                onClick={openLoginModal}
              >
                <LogIn className="h-4 w-4 mr-2" />
                로그인하고 설정 저장
              </Button>
            )}
          </div>
        </div>
      )}

      {/* 메시지 미리보기 영역 */}
      <div className="space-y-3 pt-1">
        {user && templates.length >= 1 && (
          <div
            role="tablist"
            aria-label="메시지 템플릿 탭"
            className="flex gap-1 border-b border-slate-200 mb-2 -mx-1 overflow-x-auto pretty-scrollbar"
          >
            {templates.map((t) => {
              const active = t.id === activeTemplateId;
              const isEditing = editingTabId === t.id;
              return (
                <div
                  key={t.id}
                  className={cn(
                    "inline-flex items-center gap-1 px-1 border-b-2 transition-colors duration-200",
                    active ? "border-indigo-600" : "border-transparent"
                  )}
                >
                  {isEditing ? (
                    <input
                      ref={editingInputRef}
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onBlur={commitEditingName}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          commitEditingName();
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          cancelEditingName();
                        }
                      }}
                      maxLength={30}
                      aria-label="템플릿 이름 편집"
                      className="px-2 py-1.5 text-sm font-semibold text-indigo-600 bg-white border border-indigo-300 rounded outline-none focus:ring-2 focus:ring-indigo-500 max-w-[140px]"
                    />
                  ) : (
                    <>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => handleTabClick(t.id)}
                        title={t.name}
                        className={cn(
                          "px-2 py-2 text-sm transition-colors duration-200 whitespace-nowrap max-w-[140px] truncate cursor-pointer",
                          "focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1 rounded-t",
                          active ? "text-indigo-600 font-semibold" : "text-slate-500 hover:text-slate-700"
                        )}
                      >
                        {t.name}
                      </button>
                      {/* ✏️ slot — 모든 탭에 항상 같은 너비를 차지하게 두어 active 전환 시
                          layout shift 방지. inactive 탭의 슬롯은 invisible로 숨김. */}
                      {user && (
                        <button
                          type="button"
                          aria-label="이름 변경"
                          title={active ? "이름 변경" : undefined}
                          tabIndex={active ? 0 : -1}
                          aria-hidden={!active}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (active) startEditingActiveName();
                          }}
                          className={cn(
                            "p-1 rounded transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",
                            active
                              ? "text-indigo-400 hover:text-indigo-600 cursor-pointer"
                              : "invisible pointer-events-none"
                          )}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {/* 🗑️ slot — 활성 탭에서만 시각/상호작용. inactive는 자리만 차지. */}
                      {user && (
                        <button
                          type="button"
                          aria-label="현재 템플릿 삭제"
                          title={active ? "현재 탭 삭제" : undefined}
                          tabIndex={active ? 0 : -1}
                          aria-hidden={!active}
                          disabled={active && isAtMinLimit}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (active && !isAtMinLimit) setShowDeleteConfirm(true);
                          }}
                          className={cn(
                            "p-1 rounded transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500",
                            active
                              ? cn(
                                  "cursor-pointer",
                                  isAtMinLimit
                                    ? "text-slate-300 cursor-not-allowed"
                                    : "text-slate-400 hover:text-red-600"
                                )
                              : "invisible pointer-events-none"
                          )}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              );
            })}
            {/* 탭 끝: + 새 템플릿 (글로벌 추가 액션 — 어떤 탭과도 무관하므로 끝에 배치). */}
            {user && (
              <button
                type="button"
                onClick={handleOpenCreateDialog}
                disabled={isAtMaxLimit}
                title={isAtMaxLimit ? "최대 3개까지 만들 수 있습니다" : "새 템플릿 추가"}
                aria-label="새 템플릿 추가"
                className="ml-1 p-2 text-slate-400 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-slate-400 cursor-pointer rounded transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
              >
                <Plus className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
        <h3 className="text-base font-semibold text-gray-700 mb-1">생성된 메시지 미리보기:</h3>
        {subscriptionPlanId === 'free' ? (
          <div className="relative group">
            <div className="filter blur-sm">
              <div className={cn(
                "p-4 rounded-lg border border-gray-300 text-sm shadow-sm bg-gray-50/50",
                "min-h-[200px] max-h-[400px] overflow-y-auto pretty-scrollbar"
              )}>
                <div
                  className="whitespace-pre-wrap font-normal text-gray-800 leading-relaxed font-mono"
                  dangerouslySetInnerHTML={{
                    __html: hasProducts
                      ? generatedMessage
                      : generateEmptyProductsMessage()
                  }}
                />
              </div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center bg-slate-900/30 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg">
              <div className="text-center p-5 bg-white/90 backdrop-blur-md rounded-xl shadow-2xl max-w-xs mx-auto">
                <AlertCircle className="mx-auto h-10 w-10 text-amber-500" />
                <p className="mt-3 text-sm font-semibold text-slate-700">
                  메시지 미리보기는 스탠다드 플랜 이상부터 제공됩니다.
                </p>
                <Button
                  className="mt-4 bg-[#15B8A6] hover:bg-[#12a193] text-white font-medium text-sm px-4 py-2 rounded-lg transition-colors"
                  onClick={() => {
                    window.location.href = '/mypage';
                  }}
                >
                  구독 플랜 보러가기
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="relative group">
            <div className={cn(
              "p-4 rounded-lg border border-gray-300 text-sm shadow-sm bg-gray-50/50",
              "min-h-[200px] max-h-[400px] overflow-y-auto pretty-scrollbar",
              (isCopyLimitReached || isPastDue) && "filter blur-sm"
            )}>
              <div
                className="whitespace-pre-wrap font-normal text-gray-800 leading-relaxed font-mono"
                dangerouslySetInnerHTML={{
                  __html: hasProducts
                    ? generatedMessage
                    : generateEmptyProductsMessage()
                }}
              />
            </div>
            {isPastDue ? (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-900/30 backdrop-blur-sm rounded-lg">
                <div className="text-center p-5 bg-white/90 backdrop-blur-md rounded-xl shadow-2xl max-w-xs mx-auto">
                  <AlertCircle className="mx-auto h-10 w-10 text-red-500" />
                  <p className="mt-3 text-sm font-semibold text-slate-700">
                    결제 실패로 기능이 제한되었습니다.
                  </p>
                  <p className="mt-2 text-xs text-slate-500">
                    카드 재등록 또는 결제 재시도 후 다시 이용해주세요.
                  </p>
                  <Button
                    className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-sm px-4 py-2 rounded-lg transition-colors"
                    onClick={() => {
                      window.location.href = '/mypage';
                    }}
                  >
                    마이페이지로 이동
                  </Button>
                </div>
              </div>
            ) : (
              isCopyLimitReached && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900/30 backdrop-blur-sm rounded-lg">
                  <div className="text-center p-5 bg-white/90 backdrop-blur-md rounded-xl shadow-2xl max-w-xs mx-auto">
                    <AlertCircle className="mx-auto h-10 w-10 text-red-500" />
                    <p className="mt-3 text-sm font-semibold text-slate-700">
                      이번 달 복사 횟수 한도를 모두 사용하셨습니다.
                    </p>
                    <p className="mt-2 text-xs text-slate-500">
                      프리미엄 플랜으로 업그레이드시 다음 결제일까지 남은 기간에 대한 추가요금만 결제됩니다.
                    </p>
                    <Button
                      className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-sm px-4 py-2 rounded-lg transition-colors"
                      onClick={() => {
                        window.location.href = '/mypage';
                      }}
                    >
                      구독 플랜 변경하기
                    </Button>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>

      {/* 템플릿 편집 패널 */}
      {displayTemplateEditor && (
        <div className="bg-slate-50 rounded-xl p-5 border border-slate-200 shadow-sm space-y-4">
          {/* 메시지에 추가할 수 있는 정보. placeholder가 이미 예시를 보여주므로 default는 접힘.
              펼치면 각 chip을 클릭해 textarea 커서 위치에 자동 삽입 가능. */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setShowVariablesPanel(v => !v)}
              aria-expanded={showVariablesPanel}
              className="w-full flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-inset"
            >
              <div className="text-left">
                <h4 className="text-sm font-semibold text-gray-700">
                  메시지에 추가할 수 있는 정보
                </h4>
                {!showVariablesPanel && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    [상품목록] [총건수] [금액] [배송비] [총금액] [입금계좌] [고객 닉네임]
                  </p>
                )}
              </div>
              <ChevronDown
                className={cn(
                  "h-4 w-4 text-gray-400 flex-shrink-0 ml-2 transition-transform duration-200",
                  showVariablesPanel && "rotate-180"
                )}
              />
            </button>
            {showVariablesPanel && (
              <div className="px-3 pb-3 pt-1 border-t border-gray-100">
                <p className="text-xs text-gray-500 mb-2 mt-2">아래 항목을 클릭하면 메시지에 추가됩니다.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                  {templateVariables.map((variable) => (
                    <button
                      key={variable.code}
                      type="button"
                      onClick={() => handleInsertVariable(variable.code)}
                      className="flex items-center text-left p-1 rounded hover:bg-indigo-50 cursor-pointer transition-colors duration-200 group"
                    >
                      <code className="font-mono text-sm text-indigo-600 bg-indigo-50 group-hover:bg-indigo-100 px-1.5 py-0.5 rounded-md mr-2 whitespace-nowrap flex-shrink-0 transition-colors duration-200">
                        {variable.code}
                      </code>
                      <span className="text-xs text-gray-600">{variable.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Textarea
            ref={textareaRef}
            value={template}
            onChange={(e) => handleTemplateChange(e.target.value)}
            placeholder={EXAMPLE_TEMPLATE}
            className="h-48 font-mono text-sm p-3 border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 resize-y"
          />

          {/* 상품 목록 이모지 설정 */}
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <label htmlFor="itemEmojiInput" className="text-sm font-semibold text-gray-700 block mb-1.5">상품 이름 앞에 붙일 아이콘</label>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                id="itemEmojiInput"
                value={memberSetting.itemEmoji}
                onChange={(e) => {
                  applyEmoji(e.target.value.slice(0, 2)); // Limit emoji length
                }}
                placeholder="예: 🛍️"
                className="w-28 h-9 text-sm px-3 py-2 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <div className="flex gap-1.5 flex-wrap">
                {["🛍️", "✅", "📌", "🔸", "✨", "➡️"].map(emoji => (
                  <Button
                    key={emoji}
                    variant="outline"
                    size="sm"
                    className="px-2.5 py-1 h-9 text-gray-700 border-gray-300 hover:bg-gray-100"
                    onClick={() => {
                      applyEmoji(emoji);
                    }}
                  >
                    {emoji}
                  </Button>
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1.5">
              상품 목록 각 줄 앞에 붙일 아이콘이에요. 비워두거나 원하는 이모티콘을 선택하세요. (선택)
            </p>
          </div>

        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 w-full mt-4 pt-4 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          className="flex-1 order-2 sm:order-1 text-sm px-4 py-2.5 text-gray-700 border-gray-300 hover:bg-gray-100 hover:border-gray-400 transition-colors"
          onClick={resetOrder}
          disabled={!hasProducts}
        >
          상품 목록 초기화
        </Button>
        <Button
          ref={buttonRef}
          type="button"
          className="flex-1 order-1 sm:order-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2.5 rounded-md transition-colors duration-150 ease-in-out"
          onClick={handleCopyToClipboard}
          disabled={!hasProducts}
          data-tour="settlement-area"
        >
          {isCopied ? (
            <>
              <Check className="h-4 w-4 mr-1.5" />
              복사 완료!
            </>
          ) : (
            <>
              <Copy className="h-4 w-4 mr-1.5" />
              메시지 복사하기
            </>
          )}
        </Button>
      </div>
      {/* This div is the one that contains all the content like settings panels, preview, and buttons */}

      {/* 비회원 복사 제한 다이얼로그 */}
      <AlertDialog open={showNonMemberCopyLimitDialog} onOpenChange={setShowNonMemberCopyLimitDialog}>
        <AlertDialogContent className="bg-white rounded-xl shadow-lg max-w-md mx-auto">
          <AlertDialogHeader className="space-y-3">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
              <LockKeyhole className="h-6 w-6 text-yellow-600" />
            </div>
            <AlertDialogTitle className="text-xl font-semibold text-center text-gray-900">오늘의 무료 복사 횟수 초과</AlertDialogTitle>
            <AlertDialogDescription className="text-center text-gray-600 text-sm">
              간편 로그인하시면 7일 동안 무료로 프리미엄 플랜 체험이 가능합니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2 pt-4">
            <AlertDialogCancel className="w-full sm:w-auto border-gray-300 hover:bg-gray-100">다음에 할게요</AlertDialogCancel>
            <AlertDialogAction
              className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={() => {
                setShowNonMemberCopyLimitDialog(false);
                openLoginModal?.();
              }}
            >
              로그인하기
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 회원 복사 제한 다이얼로그 */}
      <AlertDialog open={showMemberCopyLimitDialog} onOpenChange={setShowMemberCopyLimitDialog}>
        <AlertDialogContent className="bg-white rounded-xl shadow-lg max-w-md mx-auto">
          <AlertDialogHeader className="space-y-3">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <AlertDialogTitle className="text-xl font-semibold text-center text-gray-900">복사 횟수 한도 초과</AlertDialogTitle>
            <AlertDialogDescription className="text-center text-gray-600 text-sm">
              현재 구독 플랜의 정산서 복사 횟수 한도를 모두 사용하셨습니다.
              <br />
              더 많은 사용을 원하시면 플랜을 변경해주세요.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2 pt-4">
            <AlertDialogCancel className="w-full sm:w-auto border-gray-300 hover:bg-gray-100">닫기</AlertDialogCancel>
            <AlertDialogAction
              className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={() => {
                setShowMemberCopyLimitDialog(false);
                // 마이페이지의 플랜 섹션으로 이동하도록 유도할 수 있습니다.
                // 예: window.location.href = '/mypage#plans'; (실제 URL은 프로젝트 구조에 맞게)
                // 우선은 마이페이지로만 이동시킵니다.
                window.location.href = '/mypage';
              }}
            >
              플랜 변경하기
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 템플릿 삭제 확인 다이얼로그 */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="bg-white rounded-xl shadow-lg max-w-md mx-auto">
          <AlertDialogHeader className="space-y-3">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <Trash2 className="h-6 w-6 text-red-600" />
            </div>
            <AlertDialogTitle className="text-xl font-semibold text-center text-gray-900">
              이 템플릿을 삭제할까요?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center text-gray-600 text-sm">
              삭제하면 되돌릴 수 없습니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2 pt-4">
            <AlertDialogCancel className="w-full sm:w-auto border-gray-300 hover:bg-gray-100">
              취소
            </AlertDialogCancel>
            <AlertDialogAction
              className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white"
              onClick={async () => {
                setShowDeleteConfirm(false);
                // 보류 중인 debounce save를 먼저 flush — activeId가 새로 바뀐 뒤
                // 옛 입력이 잘못된 템플릿을 덮어쓰는 race 차단.
                await flushPendingContentSave();
                try {
                  await onDeleteActive();
                } catch {
                  toast.error('템플릿 삭제에 실패했습니다.');
                }
              }}
            >
              삭제
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 새 템플릿 만들기 다이얼로그 — window.prompt() 대체.
          Enter로 제출, 빈 이름 시 "추가" 버튼 disabled, 닫힐 때 state cleanup. */}
      <AlertDialog
        open={showCreateDialog}
        onOpenChange={(open) => {
          setShowCreateDialog(open);
          if (!open) setNewTemplateName('');
        }}
      >
        <AlertDialogContent
          className="bg-white rounded-xl shadow-lg max-w-md mx-auto"
          onOpenAutoFocus={(e) => {
            // Radix는 기본적으로 첫 focusable element(Cancel)에 포커스를 둬서
            // Enter가 dialog dismiss로 흘러감. preventDefault 후 input에 직접 focus.
            e.preventDefault();
            const input = newTemplateInputRef.current;
            if (input) {
              input.focus();
              input.select();
            }
          }}
        >
          <AlertDialogHeader className="space-y-3">
            <AlertDialogTitle className="text-xl font-semibold text-gray-900">
              새 템플릿 만들기
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600 text-sm">
              탭으로 전환할 새 메시지 템플릿을 추가합니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!newTemplateName.trim()) return;
              handleConfirmCreateTemplate();
            }}
          >
            <Input
              ref={newTemplateInputRef}
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              maxLength={30}
              placeholder="예: 결제완료 안내"
              aria-label="새 템플릿 이름"
              className="w-full text-sm px-3 py-2 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
            <AlertDialogFooter className="flex-col sm:flex-row gap-2 pt-4">
              <AlertDialogCancel
                type="button"
                className="w-full sm:w-auto border-gray-300 hover:bg-gray-100"
              >
                취소
              </AlertDialogCancel>
              {/* AlertDialogAction이 아닌 plain Button 사용 — AlertDialogAction은 Radix의
                  close 컨트롤이라 Enter 시 form submit과 dialog close가 race될 수 있음.
                  dialog close는 handleConfirmCreateTemplate 내부에서 수동으로 처리. */}
              <Button
                type="submit"
                disabled={!newTemplateName.trim()}
                className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50 disabled:cursor-not-allowed mt-2 sm:mt-0"
              >
                추가
              </Button>
            </AlertDialogFooter>
          </form>
        </AlertDialogContent>
      </AlertDialog>

      {/* past_due 다이얼로그 */}
      <AlertDialog open={showPastDueDialog} onOpenChange={setShowPastDueDialog}>
        <AlertDialogContent className="bg-white rounded-xl shadow-lg max-w-md mx-auto">
          <AlertDialogHeader className="space-y-3">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <AlertDialogTitle className="text-xl font-semibold text-center text-gray-900">결제 실패로 기능이 제한되었습니다</AlertDialogTitle>
            <AlertDialogDescription className="text-center text-gray-600 text-sm">
              정기 결제가 실패하여 구독이 연체 상태입니다. 카드 재등록 또는 결제 재시도 후 다시 이용해주세요.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2 pt-4">
            <AlertDialogCancel className="w-full sm:w-auto border-gray-300 hover:bg-gray-100">닫기</AlertDialogCancel>
            <AlertDialogAction
              className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={() => {
                setShowPastDueDialog(false);
                window.location.href = '/mypage';
              }}
            >
              마이페이지로 이동
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export const MessageTemplateSection = memo(MessageTemplateSectionImpl);